import { useRef, useState, useEffect, useId } from 'react';
import { Input, InputGroup, InputGroupText, Tooltip } from "reactstrap";
import { formatToGbIsoDateTime } from "../utils/dateUtils";
import { parseUserInputtedNumber } from '../features/quotes/functions/quoteFunctions';
import { formatNumber } from '../utils/numberUtils';
import { useLockState } from "./userLocks/LockCheck";
import { useCostLock } from "../hooks/useCostLock";
import CurrencyIcon from '../features/quotes/components/CurrencyIcon';

export const TextInput = ({value, isEditable, onChange, ...rest}) => {
    const lockState = useLockState();

    return (
        <div style={{ width: "100%" }}>
            <Input className={lockState?.isLocked ? "locked" : ""} value={value || ""} onChange={onChange} disabled={!isEditable} {...rest}/>
        </div>
    )
};

export const FormattedNumberField = ({name, value, disabled, invalid, id, numDecimalPlaces, onChange}) => {   
    const rawValue = value;
    const [inputValue, setInputValue] = useState(!isNaN(rawValue) ? rawValue : '');
    const [hasFocus, setHasFocus] = useState(false);
    const lockState = useLockState();
    const isCostLocked = useCostLock(name);

    useEffect(() => {
        if (!isNaN(rawValue) && rawValue !== null && rawValue !== undefined) {
            if (!hasFocus) {
                setInputValue(formatNumber(rawValue, Math.min(numDecimalPlaces, 2)));
            }
        } else {
            setInputValue('');
        }
    }, [rawValue]);

    // Allow typing without formatting
    const handleChange = (e) => {
        var processedInput = parseUserInputtedNumber(e.target.value, numDecimalPlaces)
        setInputValue(processedInput);
        onChange?.({ ...e, target: { ...e.target, name, id, value: Number(processedInput), valueAsNumber: Number(processedInput) } });
    };

    // Format on blur
    const handleBlur = () => {
        setHasFocus(false)

        const num = Number(inputValue);
        if (!isNaN(num)) {
            setInputValue(formatNumber(num, Math.min(numDecimalPlaces, 2)));
        } else {
            setInputValue('');
        }
    };

    // Unformat on focus
    const handleFocus = () => {
        setHasFocus(true)
        setInputValue(!isNaN(rawValue) ? rawValue : '');
    };

    return  (
        <div style={{ width: "100%" }}>
            <InputGroup style={{ width: "9rem" }}>
                <TextInput
                    id={id}
                    name={name}
                    className={lockState?.isLocked || isCostLocked ? "locked" : ""}
                    invalid={invalid}
                    isEditable={!disabled}
                    value={inputValue}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                />
            </InputGroup>
        </div>
    )
}

    export const CurrencyInput = ({ name, value, unformattedValue = null, originalValue = null, latestValue = null, isEditable, onChange, errorMessage, style, className, borderColor, showUpArrow, showDownArrow, title, id, allowUndo = false, onClickUndo, ...rest}) => {
    const inputRef = useRef(null);
    const lockState = useLockState();
    const isCostLocked = useCostLock(name);
    const [iconHover, setIconHover] = useState(false);
    const generatedId = useId();
    const tooltipId = id ?? generatedId;

    const handleFocus = () => {
        if (inputRef.current) {
            inputRef.current.select();
        }
    };

    return (
        <>
            <div
                {...(tooltipId ? { id: tooltipId } : {})}
                style={{ width: "100%" }}
                onMouseEnter={() => setIconHover(true)}
                onMouseLeave={() => setIconHover(false)}
            >
                <InputGroup className={className}>
                    <CurrencyIcon 
                        isEditable={isEditable} 
                        style={{backgroundColor: borderColor}} 
                        allowUndo={allowUndo} 
                        onClickUndo={() => onClickUndo?.()}
                    />
                    {showUpArrow &&
                        <i
                            className={`bi bi-arrow-up-short position-absolute text-success fs-5 ms-1 ${lockState?.isLocked || isCostLocked ? "locked" : ""}`} 
                            style={{
                                top: "16px",
                                left: "34px",
                                transform: "translateY(-50%)",
                                pointerEvents: "none",
                                zIndex: 2
                            }}
                        />
                    }
                    {showDownArrow &&
                        <i
                            className={`bi bi-arrow-down-short position-absolute fs-5 ms-1 text-danger ${lockState?.isLocked || isCostLocked ? "locked" : ""}`} 
                            style={{
                                bottom: "-16px",
                                left: "34px",
                                transform: "translateY(-50%)",
                                pointerEvents: "none",
                                zIndex: 2
                            }}
                        />
                    }
                    <Input
                        className={`text-end no-arrows ${lockState?.isLocked || isCostLocked ? "locked" : ""}`}
                        type="number" 
                        value={value ?? ""}
                        onChange={onChange}
                        disabled={!isEditable}
                        innerRef={inputRef}
                        onFocus={handleFocus}
                        invalid={errorMessage}
                        style={style}
                        {...rest}
                    />
                </InputGroup>
            </div>
            {title && iconHover && tooltipId && (
                <Tooltip isOpen={true} target={tooltipId} placement="top">{title}</Tooltip>
            )}
            {errorMessage && (
                    <div className="invalid-feedback d-block">{errorMessage}</div>
            )}
        </>
    )
};

export const NumberInput = ({ name, value, isEditable, type, onChange, isInvalid, borderColor, showUpArrow, showDownArrow, ...rest }) => {
    const inputRef = useRef(null);
    const lockState = useLockState();
    const isCostLocked = useCostLock(name);
    
    const handleFocus = () => {
        if (inputRef.current) {
            inputRef.current.select();
        }
    };

    return (
        <div style={{ width: "100%" }}>
            <InputGroup>
                { type?.toLowerCase() === "currency" && 
                    <InputGroupText className={!isEditable ? "input-group-text-disabled" : ""}  style={{backgroundColor: borderColor}}>
                        £
                    </InputGroupText>
                } 
                {showUpArrow &&
                    <i
                        className={`bi bi-arrow-up-short position-absolute text-success fs-5 ms-1 ${lockState?.isLocked || isCostLocked ? "locked" : ""}`}
                        style={{
                            top: "16px",
                            left: type.toLowerCase() === "currency" ? "34px" : "auto",
                            transform: "translateY(-50%)",
                            pointerEvents: "none",
                            zIndex: 2
                        }}
                    />
                }
                {showDownArrow &&
                    <i
                        className={`bi bi-arrow-down-short position-absolute fs-5 ms-1 text-danger ${lockState?.isLocked || isCostLocked ? "locked" : ""}`}
                        style={{
                            bottom: "-16px",
                            left: type.toLowerCase() === "currency" ? "34px" : "auto",
                            transform: "translateY(-50%)",
                            pointerEvents: "none",
                            zIndex: 2
                        }}
                    />
                }

                <Input
                    name={name}
                    type="number"
                    value={value ?? ""}
                    onChange={onChange}
                    disabled={!isEditable}
                    innerRef={inputRef}
                    onFocus={handleFocus}
                    invalid={isInvalid}
                    {...rest}
                    className={`text-end no-arrows ${lockState?.isLocked || isCostLocked ? "locked" : ""}`}
                />
                { type?.toLowerCase() === "percentage" && 
                    <InputGroupText className={lockState?.isLocked || isCostLocked ? "locked" : ""}  style={{backgroundColor: borderColor}}>
                        %
                    </InputGroupText>   
                } 
            </InputGroup>
        </div>
    )
};

export const DateTimeInput = ({value, isEditable, onChange, ...rest}) => {
    const lockState = useLockState();

    return (
        <Input
            type="datetime-local"
            className={lockState?.isLocked ? "locked" : ""}
            value={formatToGbIsoDateTime(value) || ""}
            onChange={onChange}
            // onKeyDown={(e) => e.preventDefault()}
            // onMouseDown={(e) => e.preventDefault()}
            // onPaste={(e) => e.preventDefault()}
            // onFocus={(e) => e.target.blur()}
            // tabIndex={-1}
            disabled={!isEditable}
            {...rest}
        />
    )
};

export const PercentageInput = ({name, value, isEditable, onChange, errorMessage, style, className, borderColor, ...rest}) => {
    const inputRef = useRef(null);
    const lockState = useLockState();
    const isCostLocked = useCostLock(name);

    const handleFocus = () => {
        if (inputRef.current) {
            inputRef.current.select();
        }
    };

    return (
        <>
            <div style={{ width: "100%" }}>
                <InputGroup style={style} className={className}>
                    <Input
                        className={`text-end no-arrows ${lockState?.isLocked || isCostLocked ? "locked" : ""}`} 
                        type="number"
                        value={value ?? ""}
                        onChange={onChange}
                        disabled={!isEditable}
                        innerRef={inputRef}
                        onFocus={handleFocus}
                        invalid={errorMessage}
                        {...rest}
                    />
                    <InputGroupText className={lockState?.isLocked || isCostLocked ? "locked" : ""}  style={{backgroundColor: borderColor}}>
                        %
                    </InputGroupText>
                </InputGroup>
            </div>
            {errorMessage && (
                    <div className="invalid-feedback d-block">{errorMessage}</div>
            )}
        </>
    )
};
